import { useEffect, useState } from 'react'
import { BrandKitBlockPicker, BrandKitChannelPicker } from './BrandKitBlockPicker'
import { DEFAULT_BRAND_KIT_BLOCKS, type BrandKitBlockId } from './brandKitBlocks'
import { ALL_PLATFORMS } from './sections/platformDefault'
import type { ChannelSubs, Platform } from './creatorsApi'
import { Link } from 'react-router-dom'
import { issueBrandLink, fmtDateTime, type IssuedBrandLink } from './brandLinksApi'

export function BrandLinkModal({
  open,
  creatorId,
  onClose,
  channels,
}: {
  open: boolean
  creatorId: string
  onClose: () => void
  /** 채널 선택지 — 크리에이터가 가진 채널만 고를 수 있게 한다. */
  channels?: ChannelSubs[]
}) {
  const [brandName, setBrandName] = useState('')
  const [busy, setBusy] = useState(false)
  const [issued, setIssued] = useState<IssuedBrandLink | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [expiresPreview, setExpiresPreview] = useState('')
  const [blocks, setBlocks] = useState<BrandKitBlockId[]>(DEFAULT_BRAND_KIT_BLOCKS)
  const [platforms, setPlatforms] = useState<Platform[]>(ALL_PLATFORMS)

  useEffect(() => {
    if (open) {
      setBrandName('')
      setIssued(null)
      setError(null)
      setCopied(false)
      setBlocks(DEFAULT_BRAND_KIT_BLOCKS)
      setPlatforms(ALL_PLATFORMS)
      setExpiresPreview(fmtDateTime(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()))
    }
  }, [open])

  if (!open) return null

  const submit = async () => {
    if (!brandName.trim()) {
      setError('Enter the recipient brand.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      setIssued(await issueBrandLink(creatorId, brandName.trim(), blocks, platforms))
    } catch (e) {
      setError(e instanceof Error ? e.message : '발급에 실패했습니다.')
    } finally {
      setBusy(false)
    }
  }

  const copy = async () => {
    if (!issued) return
    try {
      await navigator.clipboard.writeText(issued.url)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-800/40" onClick={onClose}>
      <div
        className="max-h-[86vh] w-[460px] overflow-y-auto border border-line-soft bg-paper p-6"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {!issued ? (
          <>
            <div className="font-display text-lg font-medium text-ink-800">Generate brand link</div>
            <label className="mt-4 block">
              <span className="font-text text-[11px] font-medium tracking-[0.14em] text-fg-3">RECIPIENT BRAND</span>
              <input
                className="mt-1 h-[38px] w-full border border-line-soft bg-white px-3 font-text text-[13px]"
                placeholder="e.g. 아모레퍼시픽"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
              />
            </label>
            <div className="mt-4">
              <BrandKitChannelPicker value={platforms} onChange={setPlatforms} channels={channels} />
            </div>
            <div className="mt-4">
              <BrandKitBlockPicker value={blocks} onChange={setBlocks} external />
            </div>
            <p className="mt-3 font-text text-[12px] text-fg-3">
              Valid for 30 days (until {expiresPreview}). This link does not include revenue or
              confirmed business data.
            </p>
            {error && <p className="mt-2 font-text text-[12px] text-status-danger" role="alert">{error}</p>}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button className="h-[36px] border border-line-soft bg-white px-4 font-text text-[13px]" onClick={onClose}>
                Cancel
              </button>
              <button
                className="h-[36px] bg-brand-blue px-4 font-text text-[13px] font-medium text-white disabled:opacity-50"
                disabled={busy}
                onClick={() => void submit()}
              >
                {busy ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="font-display text-lg font-medium text-ink-800">Link created</div>
            <div className="mt-4 flex gap-2">
              <input
                readOnly
                className="h-[38px] flex-1 border border-line-soft bg-white px-3 font-text text-[12px]"
                value={issued.url}
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                className="h-[38px] w-[84px] bg-brand-blue text-center font-text text-[13px] font-medium text-white"
                onClick={() => void copy()}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-3 font-text text-[12px] text-fg-3">Expires {fmtDateTime(issued.expiresAt)}</p>
            <div className="mt-5 flex items-center justify-between">
              <Link to="/creators/brand-links" className="font-text text-[12px] font-medium text-brand-blue">
                View all links →
              </Link>
              <button className="h-[36px] border border-line-soft bg-white px-4 font-text text-[13px]" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
